import React from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

interface OdometerDisplayProps {
  value: number | null | undefined;
  source?: 'smartcar' | 'obd' | 'gps_estimate' | null;
  updatedAt?: string | null;
  /** When true, renders in a compact single-row variant suitable for table cells */
  compact?: boolean;
}

/**
 * Renders an odometer reading in the style of a physical roller-counter:
 *   - Leading zeros in light gray
 *   - Significant digits in bold dark
 *   - "km" suffix in normal weight
 *
 * Below the digits, shows the data source and relative age ("il y a Xh").
 */
export const OdometerDisplay: React.FC<OdometerDisplayProps> = ({
  value,
  source,
  updatedAt,
  compact = false,
}) => {
  if (value == null) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  // Pad to 6 digits, split into leading zeros and significant digits
  const padded = String(Math.round(value)).padStart(6, '0');
  // Find the index where significant digits start (first non-zero)
  const firstSignificantIdx = padded.search(/[1-9]/) === -1 ? 5 : padded.search(/[1-9]/);

  const leadingZeros = padded.slice(0, firstSignificantIdx);
  const significantDigits = padded.slice(firstSignificantIdx);

  // Relative age
  let ageLabel: string | null = null;
  if (updatedAt) {
    try {
      ageLabel = formatDistanceToNow(parseISO(updatedAt), { addSuffix: true, locale: fr });
    } catch {
      ageLabel = null;
    }
  }

  // Source label
  const sourceInfo = (() => {
    if (!source) return null;
    if (source === 'smartcar') return { icon: '🔗', label: 'Smartcar' };
    if (source === 'obd') return { icon: '📡', label: 'OBD' };
    if (source === 'gps_estimate') return { icon: null, label: '~GPS' };
    return null;
  })();

  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      {/* Odometer digits */}
      <div
        className="font-mono tabular-nums inline-flex items-baseline leading-none select-none"
        aria-label={`${value.toLocaleString('fr-FR')} km`}
      >
        {leadingZeros.length > 0 && (
          <span className={compact ? 'text-gray-300 text-sm font-normal' : 'text-gray-300 text-base font-normal'}>
            {leadingZeros}
          </span>
        )}
        <span className={compact ? 'text-gray-900 font-bold text-sm' : 'text-gray-900 font-bold text-base'}>
          {significantDigits}
        </span>
        <span className={compact ? 'text-gray-500 font-normal text-xs ml-1' : 'text-gray-500 font-normal text-sm ml-1'}>
          km
        </span>
      </div>

      {/* Source + age */}
      {(sourceInfo || ageLabel) && (
        <div className="flex items-center gap-1 text-xs text-gray-400 leading-none">
          {sourceInfo && (
            <span className="flex items-center gap-0.5">
              {sourceInfo.icon && <span>{sourceInfo.icon}</span>}
              <span>{sourceInfo.label}</span>
            </span>
          )}
          {sourceInfo && ageLabel && <span className="text-gray-300">·</span>}
          {ageLabel && <span>{ageLabel}</span>}
        </div>
      )}
    </div>
  );
};
